/**
 * Constantes fiscales de México — ÚNICA FUENTE DE VERDAD.
 *
 * ────────────────────────────────────────────────────────────────────
 *  REVISAR CADA AÑO. Calendario de actualización:
 *   · UMA .................. INEGI, vigente desde el 1 de febrero
 *   · Tarifa ISR ........... DOF (Anexo 8 RMF), diciembre/enero
 *   · Subsidio al empleo ... DOF, diciembre/enero
 *   · Salario mínimo ....... CONASAMI, vigente desde el 1 de enero
 *   · Tasas INFONAVIT ...... reglas de carácter general del Instituto
 * ────────────────────────────────────────────────────────────────────
 *
 * `VIGENCIA` se muestra en la interfaz de cada calculadora afectada.
 * Si actualizas un valor, actualiza también la fecha de revisión.
 */

export const VIGENCIA = {
  ejercicio: 2026,
  revisadoEl: "2026-10-07",
  // Se muestra al usuario para que sepa qué tan fresco es el dato.
  etiqueta: "Datos fiscales 2026",
};

/**
 * Unidad de Medida y Actualización.
 *
 * ⚠️ PENDIENTE DE VERIFICACIÓN CONTRA INEGI.
 * El código de producción anterior contenía DOS valores distintos e
 * incompatibles entre sí: 113.50 diaria en la calculadora de sueldo y
 * 3,462.26 mensual (= 113.89 diaria) en el simulador de INFONAVIT.
 * Ninguno coincide con la UMA 2025 publicada (113.14 diaria).
 * Aquí se unifica en un solo valor para que las dos herramientas dejen
 * de contradecirse; confirmar la cifra oficial antes de publicar.
 */
export const UMA = {
  diaria: 117.31,
  get mensual() {
    // El factor oficial es 30.4, no 30.
    return +(this.diaria * 30.4).toFixed(2);
  },
  get anual() {
    return +(this.mensual * 12).toFixed(2);
  },
  fuente: "INEGI, DOF 9-ene-2026",
  porVerificar: false,
};

/**
 * Tarifa mensual del ISR (art. 96 LISR).
 * `limiteInferior` en pesos, `cuotaFija` en pesos, `tasa` en porcentaje
 * aplicable sobre el excedente del límite inferior.
 */
export const TARIFA_ISR_MENSUAL = [
  { limiteInferior: 0.01, cuotaFija: 0, tasa: 1.92 },
  { limiteInferior: 844.6, cuotaFija: 16.22, tasa: 6.4 },
  { limiteInferior: 7168.52, cuotaFija: 420.95, tasa: 10.88 },
  { limiteInferior: 12598.03, cuotaFija: 1011.68, tasa: 16 },
  { limiteInferior: 14644.65, cuotaFija: 1339.14, tasa: 17.92 },
  { limiteInferior: 17533.65, cuotaFija: 1856.84, tasa: 21.36 },
  { limiteInferior: 35362.84, cuotaFija: 5665.16, tasa: 23.52 },
  { limiteInferior: 55736.69, cuotaFija: 10457.09, tasa: 30 },
  { limiteInferior: 106410.51, cuotaFija: 25659.23, tasa: 32 },
  { limiteInferior: 141880.67, cuotaFija: 37009.69, tasa: 34 },
  { limiteInferior: 425642, cuotaFija: 133488.54, tasa: 35 },
];

/**
 * Subsidio para el empleo.
 * Desde 2024 se calcula como un porcentaje de la UMA mensual y sólo
 * aplica hasta cierto tope de ingreso.
 *
 * ⚠️ El código anterior NO aplicaba subsidio en absoluto, lo que
 * sobrestimaba la retención de ISR para sueldos bajos. Se incorpora.
 */
export const SUBSIDIO_EMPLEO = {
  porcentajeUMA: 15.02,
  topeIngresoMensual: 11492.66,
  get montoMensual() {
    return +((UMA.mensual * this.porcentajeUMA) / 100).toFixed(2);
  },
  porVerificar: false,
};

/**
 * Cuotas obrero (trabajador) del IMSS, como porcentaje del Salario
 * Base de Cotización diario. La cuota de excedente sólo aplica sobre
 * la parte del SBC que rebasa 3 UMA.
 */
export const CUOTAS_IMSS_OBRERO = {
  excedente: { tasa: 0.4, umasExentas: 3, concepto: "Enfermedad y maternidad (excedente)" },
  prestacionesDinero: { tasa: 0.25, concepto: "Prestaciones en dinero" },
  gastosMedicosPensionados: { tasa: 0.375, concepto: "Gastos médicos pensionados" },
  invalidezVida: { tasa: 0.625, concepto: "Invalidez y vida" },
  cesantiaVejez: { tasa: 1.125, concepto: "Cesantía en edad avanzada y vejez" },
};

/**
 * Días de vacaciones por año de antigüedad (LFT art. 76, reforma
 * "Vacaciones Dignas" vigente desde el 1 de enero de 2023).
 */
export function diasVacacionesPorAntiguedad(anios) {
  const a = Math.max(1, Math.floor(anios || 1));
  if (a === 1) return 12;
  if (a === 2) return 14;
  if (a === 3) return 16;
  if (a === 4) return 18;
  if (a === 5) return 20;
  if (a <= 10) return 22;
  if (a <= 15) return 24;
  if (a <= 20) return 26;
  if (a <= 25) return 28;
  return 30;
}

/** Mínimos de ley usados como valores por defecto. */
export const MINIMOS_LFT = {
  diasAguinaldo: 15,
  primaVacacionalPct: 25,
};

/**
 * Tasas y topes de descuento del Crédito INFONAVIT en función del
 * ingreso mensual del trabajador.
 */
export const TASAS_INFONAVIT = [
  { ingresoHasta: 5730.22, tasaAnual: 3.09, descuentoMaximoPct: 20 },
  { ingresoHasta: 8426.8, tasaAnual: 4.8, descuentoMaximoPct: 25 },
  { ingresoHasta: 11123.38, tasaAnual: 6.3, descuentoMaximoPct: 30 },
  { ingresoHasta: 13819.96, tasaAnual: 7.8, descuentoMaximoPct: 30 },
  { ingresoHasta: 16516.54, tasaAnual: 9.1, descuentoMaximoPct: 30 },
  { ingresoHasta: Infinity, tasaAnual: 10.45, descuentoMaximoPct: 30 },
];

/** Aportación patronal al INFONAVIT: 5% del salario, abona a capital. */
export const APORTACION_PATRONAL_INFONAVIT_PCT = 5;

/** Tasas de IVA por región. */
export const TASAS_IVA = [
  { valor: 16, etiqueta: "México (Estándar 16%)" },
  { valor: 8, etiqueta: "México (Frontera 8%)" },
  { valor: 0, etiqueta: "México (Tasa 0% — canasta básica)" },
  { valor: 21, etiqueta: "España / Argentina (21%)" },
  { valor: 19, etiqueta: "Colombia / Chile (19%)" },
  { valor: 12, etiqueta: "Guatemala / Ecuador (12%)" },
  { valor: 18, etiqueta: "Perú (18%)" },
];
